import React, { useState } from 'react';
import { useThemeMode } from '../../ThemeContext';
import { formatDateToAPI } from '../../helpers/UtilsFunctions';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { TextField } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { enGB } from 'date-fns/locale';
import { dataAPI } from '../../helpers/api_service';
import AppIconBlue from '../../assets/AppIconBlue.svg';
import '../../styles/loading-animations.css';

const UploadDocumentTabs = ({ isOpen, onClose, onSubmit, editData = null, selectedFolder = null }) => {
  const { mode } = useThemeMode();
  const [activeTab, setActiveTab] = useState('multiple');
  const isEditMode = !!editData;
  
  // Single document form state
  const [singleFormData, setSingleFormData] = useState({
    name: '',
    version: '',
    expiry_date: null,
    file: null
  });
  
  // Multiple document form state
  const [multipleFiles, setMultipleFiles] = useState([]);
  
  // Common state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [showFormLoading, setShowFormLoading] = useState(false);
  const [showValidation, setShowValidation] = useState(false);

  // Custom theme for Material-UI components
  const customTheme = createTheme({
    palette: {
      mode: mode,
      primary: {
        main: '#26C3C0',
      },
      background: {
        default: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
        paper: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
      },
      text: {
        primary: mode === 'dark' ? '#FFFFFF' : '#222222',
        secondary: mode === 'dark' ? '#666' : '#999',
      },
    },
    components: {
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiOutlinedInput-root': {
              borderRadius: '6px',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '& fieldset': {
                borderColor: mode === 'dark' ? '#444' : '#ddd',
                transition: 'border-color 0.2s ease',
              },
              '&:hover fieldset': {
                borderColor: '#26C3C0',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#26C3C0',
              },
            },
            '& .MuiInputLabel-root': {
              color: mode === 'dark' ? '#666' : '#999',
              fontFamily: "'Urbanist', Arial, sans-serif",
              fontSize: '14px',
              '&.Mui-focused': {
                color: '#26C3C0',
              },
            },
          },
        },
      },
    },
  });

  // Initialize single form data when editData changes
  React.useEffect(() => {
    if (editData) {
      setSingleFormData({
        name: editData.name || '',
        version: editData.version || '',
        expiry_date: editData.expiry_date ? new Date(editData.expiry_date) : null,
        file: null
      });
    } else {
      setSingleFormData({
        name: '',
        version: '',
        expiry_date: null,
        file: null
      });
    }
    setError('');
  }, [editData]);

  const handleClose = () => {
    setActiveTab('multiple');
    setSingleFormData({
      name: '',
      version: '',
      expiry_date: '',
      file: null
    });
    setMultipleFiles([]);
    setError('');
    setShowFormLoading(false);
    setShowValidation(false);
    onClose();
  };

  const handleSingleInputChange = (field, value) => {
    setSingleFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSingleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSingleFormData(prev => ({
        ...prev,
        file: file
      }));
    }
  };

  const handleMultipleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    setMultipleFiles(selectedFiles);
    setError('');
  };

  const handleMultipleDrop = (event) => {
    event.preventDefault();
    const droppedFiles = Array.from(event.dataTransfer.files);
    setMultipleFiles(droppedFiles);
    setError('');
  };

  const handleMultipleDragOver = (event) => {
    event.preventDefault();
  };

  const removeMultipleFile = (index) => {
    setMultipleFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSingleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation: file is required only for new documents
    const requiredFields = ['name', 'version', 'expiry_date'];
    if (!isEditMode) {
      requiredFields.push('file');
    }
    
    const missingFields = requiredFields.filter(field => !singleFormData[field]);
    if (missingFields.length > 0) {
      setError('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    setShowValidation(true);
    
    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('name', singleFormData.name);
      formDataToSend.append('version', singleFormData.version);
      formDataToSend.append('expiry_date', formatDateToAPI(singleFormData.expiry_date));
      
      // Include folder_id if a folder is selected
      if (isEditMode && editData.folder_id) {
        formDataToSend.append('folder_id', editData.folder_id);
      } else if (selectedFolder && selectedFolder.id) {
        formDataToSend.append('folder_id', selectedFolder.id);
      }
      
      // Only append file if it's selected
      if (singleFormData.file) {
        formDataToSend.append('file', singleFormData.file);
      }

      let response;
      if (isEditMode) {
        response = await dataAPI.editDocument(editData.id, formDataToSend, { showLoading: false });
      } else {
        response = await dataAPI.uploadDocument(formDataToSend, { showLoading: false });
      }
      
      onSubmit(response);
      handleClose();
    } catch (error) {
      console.error(`${isEditMode ? 'Edit' : 'Upload'} error:`, error);
      setError(error.message || `Failed to ${isEditMode ? 'edit' : 'upload'} document. Please try again.`);
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  const handleMultipleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (multipleFiles.length === 0) {
      setError('Please select at least one file to upload');
      return;
    }

    setIsSubmitting(true);
    setShowFormLoading(true);
    
    try {
      // Create FormData for multiple file upload
      const formDataToSend = new FormData();
      
      // Add files
      multipleFiles.forEach(file => {
        formDataToSend.append('files', file);
      });
      
      // Add compliance_tool_id (hardcoded as 1)
      formDataToSend.append('compliance_tool_id', '1');
      
      // Add folder_id if a folder is selected
      if (selectedFolder && selectedFolder.id) {
        formDataToSend.append('folder_id', selectedFolder.id);
      } else {
        formDataToSend.append('folder_id', '0');
      }

      const response = await dataAPI.uploadMultipleDocuments(formDataToSend, { showLoading: false });
      onSubmit(response);
      handleClose();
    } catch (error) {
      console.error('Multiple upload error:', error);
      setError(error.message || 'Failed to upload documents. Please try again.');
    } finally {
      setIsSubmitting(false);
      setShowFormLoading(false);
    }
  };

  // Check if single form is complete
  const requiredFields = ['name', 'version', 'expiry_date'];
  if (!isEditMode) {
    requiredFields.push('file');
  }
  const isSingleFormComplete = requiredFields.every(field => singleFormData[field]);

  if (!isOpen) return null;

  return (
    <ThemeProvider theme={customTheme}>
      <>
        {/* Backdrop */}
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            backdropFilter: 'blur(2px)'
          }}
        />
        
        {/* Modal */}
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          width: '420px',
          height: 'calc(100vh - 40px)',
          backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
          boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.3)',
          zIndex: 1001,
          overflowY: 'auto',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.3s ease-in-out',
          borderRadius: '12px'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '12px 20px 8px 20px',
            borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
          }}>
            <h2 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: mode === 'dark' ? '#FFFFFF' : '#222222',
              margin: 0,
              fontFamily: "'Urbanist', Arial, sans-serif"
            }}>
              {editData ? 'Edit Document' : 'Upload Document'}
            </h2>
            <button
              onClick={handleClose}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '6px',
                borderRadius: '4px',
                color: mode === 'dark' ? '#FFFFFF' : '#666666',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CloseIcon style={{ fontSize: '20px' }} />
            </button>
          </div>

          {/* Tabs */}
          {!editData && (
            <div style={{
              display: 'flex',
              borderBottom: `1px solid ${mode === 'dark' ? '#333' : '#e0e0e0'}`
            }}>
              <button
                onClick={() => setActiveTab('multiple')}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: activeTab === 'multiple' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
                  borderBottom: activeTab === 'multiple' ? '2px solid #26C3C0' : '2px solid transparent',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  transition: 'all 0.2s ease'
                }}
              >
                Multiple Documents
              </button>
              <button
                onClick={() => setActiveTab('single')}
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: activeTab === 'single' ? '#26C3C0' : (mode === 'dark' ? '#999' : '#666'),
                  borderBottom: activeTab === 'single' ? '2px solid #26C3C0' : '2px solid transparent',
                  fontFamily: "'Urbanist', Arial, sans-serif",
                  transition: 'all 0.2s ease'
                }}
              >
                Single Document
              </button>
            </div>
          )}

          {/* Form Loading Overlay */}
          {showFormLoading && (
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1003,
              backgroundColor: mode === 'dark' ? 'rgba(0, 0, 0, 0.7)' : 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(4px)',
              pointerEvents: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '12px'
            }}>
              {/* Fill Animation Loading Indicator */}
              <div className="fill-animation-container">
                {/* Background container */}
                <div className="fill-animation-background" />
                
                {/* Progress fill animation */}
                <div className="fill-animation-progress" />
                
                {/* AppIconBlue with pulse animation */}
                <img 
                  src={AppIconBlue} 
                  alt="Loading" 
                  className={`fill-animation-icon pulse-animation-icon`}
                />
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div style={{ padding: '16px 20px', height: 'calc(100% - 120px)', overflowY: 'auto' }}>
            {/* Error Message */}
            {error && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                backgroundColor: '#ffebee',
                border: '1px solid #f44336',
                borderRadius: '6px',
                color: '#d32f2f',
                fontSize: '14px',
                fontFamily: "'Urbanist', Arial, sans-serif"
              }}>
                {error}
              </div>
            )}

            {activeTab === 'multiple' ? (
              /* Multiple Documents Form */
              <form onSubmit={handleMultipleSubmit}>
                {/* File Upload Area */}
                <div style={{ marginBottom: '16px' }}>
                  <div 
                    style={{
                      border: `2px dashed ${mode === 'dark' ? '#444' : '#ddd'}`,
                      borderRadius: '8px',
                      padding: '16px 12px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      transition: 'border-color 0.2s ease',
                      backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                      position: 'relative',
                      minHeight: '120px',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      alignItems: 'center'
                    }}
                    onDrop={handleMultipleDrop}
                    onDragOver={handleMultipleDragOver}
                    onMouseEnter={(e) => {
                      e.target.style.borderColor = '#26C3C0';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.borderColor = mode === 'dark' ? '#444' : '#ddd';
                    }}
                  >
                    <input
                      type="file"
                      onChange={handleMultipleFileChange}
                      accept=".pdf,.doc,.docx"
                      multiple
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <CloudUploadIcon style={{
                      fontSize: '32px',
                      color: '#26C3C0',
                      marginBottom: '8px'
                    }} />
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#26C3C0',
                      marginBottom: '4px',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      Click to Upload Multiple Documents
                    </div>
                    <div style={{
                      fontSize: '11px',
                      color: mode === 'dark' ? '#999' : '#666',
                      fontFamily: "'Urbanist', Arial, sans-serif",
                      lineHeight: '1.3'
                    }}>
                      Supported file types: .pdf, .doc, .docx
                      <br />
                      Drag and drop files here or click to browse
                    </div>
                  </div>
                </div>

                {/* Selected Files List */}
                {multipleFiles.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h3 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222',
                      margin: '0 0 8px 0',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      Selected Files ({multipleFiles.length})
                    </h3>
                    <div style={{
                      height: '150px',
                      overflowY: 'auto',
                      border: `1px solid ${mode === 'dark' ? '#333' : '#ddd'}`,
                      borderRadius: '6px',
                      padding: '6px',
                      backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                      /* Hide scrollbar for webkit browsers */
                      scrollbarWidth: 'none', /* Firefox */
                      msOverflowStyle: 'none', /* IE and Edge */
                    }}>
                      <style>
                        {`
                          div::-webkit-scrollbar {
                            display: none;
                          }
                        `}
                      </style>
                      {multipleFiles.map((file, index) => (
                        <div key={index} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '6px 10px',
                          backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          borderRadius: '4px',
                          marginBottom: '3px',
                          fontSize: '13px',
                          fontFamily: "'Urbanist', Arial, sans-serif",
                          minHeight: '32px'
                        }}>
                          <span style={{
                            color: mode === 'dark' ? '#FFFFFF' : '#222222',
                            flex: 1,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            fontSize: '13px',
                            lineHeight: '1.2'
                          }}>
                            {file.name}
                          </span>
                          <button
                            type="button"
                            onClick={() => removeMultipleFile(index)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#f44336',
                              cursor: 'pointer',
                              padding: '2px 4px',
                              borderRadius: '3px',
                              fontSize: '14px',
                              fontWeight: 'bold',
                              minWidth: '20px',
                              height: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || multipleFiles.length === 0}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    backgroundColor: multipleFiles.length > 0 && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: multipleFiles.length > 0 && !isSubmitting ? 'pointer' : 'not-allowed',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    transition: 'background-color 0.2s ease',
                    boxShadow: multipleFiles.length > 0 && !isSubmitting ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (multipleFiles.length > 0 && !isSubmitting) {
                      e.target.style.backgroundColor = '#22B0AD';
                    } else {
                      e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (multipleFiles.length > 0 && !isSubmitting) {
                      e.target.style.backgroundColor = '#26C3C0';
                    } else {
                      e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
                    }
                  }}
                >
                  {isSubmitting ? 'Uploading...' : `Upload ${multipleFiles.length} Document${multipleFiles.length !== 1 ? 's' : ''}`}
                </button>
              </form>
            ) : (
              /* Single Document Form */
              <form onSubmit={handleSingleSubmit}>
                {/* Document Name */}
                <div style={{ marginBottom: '16px' }}>
                  <TextField
                    size="small"
                    required
                    label="Document Name"
                    value={singleFormData.name}
                    onChange={(e) => handleSingleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    error={showValidation && !singleFormData.name.trim()}
                    helperText={showValidation && !singleFormData.name.trim() ? 'Document name is required' : ''}
                    style={{
                      width: '100%',
                      backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        '& fieldset': {
                          borderColor: mode === 'dark' ? '#444' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                        },
                        '&.Mui-error fieldset': {
                          borderColor: '#f44336',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: mode === 'dark' ? '#999' : '#666',
                        fontSize: '14px',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#f44336',
                        fontSize: '12px',
                      },
                    }}
                  />
                </div>

                {/* Version */}
                <div style={{ marginBottom: '16px' }}>
                  <TextField
                    size="small"
                    required
                    label="Version"
                    value={singleFormData.version}
                    onChange={(e) => handleSingleInputChange('version', e.target.value)}
                    disabled={isSubmitting}
                    error={showValidation && !singleFormData.version.trim()}
                    helperText={showValidation && !singleFormData.version.trim() ? 'Version is required' : ''}
                    style={{
                      width: '100%',
                      backgroundColor: mode === 'dark' ? '#1a1a1a' : '#ffffff',
                      color: mode === 'dark' ? '#FFFFFF' : '#222222'
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                        transition: 'all 0.2s ease',
                        '& fieldset': {
                          borderColor: mode === 'dark' ? '#444' : '#ddd',
                        },
                        '&:hover fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 4px 8px rgba(38,195,192,0.2)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#26C3C0',
                          boxShadow: '0 0 0 2px rgba(38,195,192,0.1)',
                        },
                        '&.Mui-error fieldset': {
                          borderColor: '#f44336',
                        },
                      },
                      '& .MuiInputLabel-root': {
                        color: mode === 'dark' ? '#999' : '#666',
                        fontSize: '14px',
                      },
                      '& .MuiFormHelperText-root': {
                        color: '#f44336',
                        fontSize: '12px',
                      },
                    }}
                  />
                </div>

                {/* Expiry Date */}
                <div style={{ marginBottom: '16px' }}>
                  <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enGB}>
                    <DatePicker
                      value={singleFormData.expiry_date}
                      onChange={(newValue) => {
                        handleSingleInputChange('expiry_date', newValue);
                      }}
                      format="dd-MM-yyyy"
                      slotProps={{
                        textField: {
                          label: "Document Review Date",
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '6px',
                              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                              color: mode === 'dark' ? '#FFFFFF' : '#222222',
                              fontSize: '14px',
                              fontFamily: "'Urbanist', Arial, sans-serif",
                              padding: '10px 16px',
                              '& fieldset': {
                                borderColor: singleFormData.expiry_date ? '#26C3C0' : (mode === 'dark' ? '#444' : '#ddd'),
                                transition: 'border-color 0.2s ease',
                              },
                              '&:hover fieldset': {
                                borderColor: '#26C3C0',
                              },
                              '&.Mui-focused fieldset': {
                                borderColor: '#26C3C0',
                              },
                            },
                            '& .MuiInputBase-input': {
                              padding: '0',
                              height: 'auto',
                              lineHeight: 'normal',
                              backgroundColor: 'transparent',
                            },
                            '& .MuiInputBase-root': {
                              backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                            },
                            backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </div>

                {/* File Upload Area */}
                <div style={{ marginBottom: '20px' }}>
                  <div style={{
                    border: `2px dashed ${mode === 'dark' ? '#444' : '#ddd'}`,
                    borderRadius: '8px',
                    padding: '20px 16px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s ease',
                    backgroundColor: mode === 'dark' ? '#2a2a2a' : '#f8f9fa',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.borderColor = '#26C3C0';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.borderColor = mode === 'dark' ? '#444' : '#ddd';
                  }}
                  >
                    <input
                      type="file"
                      onChange={handleSingleFileChange}
                      accept=".pdf,.doc,.docx"
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        opacity: 0,
                        cursor: 'pointer'
                      }}
                    />
                    <CloudUploadIcon style={{
                      fontSize: '42px',
                      color: '#26C3C0',
                      marginBottom: '12px'
                    }} />
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#26C3C0',
                      marginBottom: '6px',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      {isEditMode ? 'Click to Upload New File (Optional)' : 'Click to Upload Document'}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: mode === 'dark' ? '#999' : '#666',
                      fontFamily: "'Urbanist', Arial, sans-serif"
                    }}>
                      Supported file types: .pdf, .doc, .docx
                      {isEditMode && <br />}
                      {isEditMode && <span style={{ color: '#26C3C0' }}>Leave empty to keep current file</span>}
                    </div>
                    {singleFormData.file && (
                      <div style={{
                        marginTop: '12px',
                        fontSize: '14px',
                        color: '#26C3C0',
                        fontFamily: "'Urbanist', Arial, sans-serif"
                      }}>
                        Selected: {singleFormData.file.name}
                      </div>
                    )}
                    {isEditMode && !singleFormData.file && editData?.file_path && (
                      <div style={{
                        marginTop: '12px',
                        fontSize: '14px',
                        color: mode === 'dark' ? '#999' : '#666',
                        fontFamily: "'Urbanist', Arial, sans-serif"
                      }}>
                        Current file: {editData.name}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting || !isSingleFormComplete}
                  style={{
                    width: '100%',
                    padding: '14px 24px',
                    backgroundColor: isSingleFormComplete && !isSubmitting ? '#26C3C0' : (mode === 'dark' ? '#444' : '#666'),
                    color: '#FFFFFF',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: '500',
                    cursor: isSingleFormComplete && !isSubmitting ? 'pointer' : 'not-allowed',
                    fontFamily: "'Urbanist', Arial, sans-serif",
                    transition: 'background-color 0.2s ease',
                    boxShadow: isSingleFormComplete && !isSubmitting ? '0 2px 8px rgba(38, 195, 192, 0.3)' : 'none',
                    opacity: isSubmitting ? 0.7 : 1
                  }}
                  onMouseEnter={(e) => {
                    if (isSingleFormComplete && !isSubmitting) {
                      e.target.style.backgroundColor = '#22B0AD';
                    } else {
                      e.target.style.backgroundColor = mode === 'dark' ? '#555' : '#777';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isSingleFormComplete && !isSubmitting) {
                      e.target.style.backgroundColor = '#26C3C0';
                    } else {
                      e.target.style.backgroundColor = mode === 'dark' ? '#444' : '#666';
                    }
                  }}
                >
                  {isSubmitting ? (isEditMode ? 'Updating...' : 'Uploading...') : (isEditMode ? 'Update Document' : 'Upload Document')}
                </button>
              </form>
            )}
          </div>
        </div>
      </>
    </ThemeProvider>
  );
};

export default UploadDocumentTabs;
